'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Modal from 'react-modal';

import allNutrients from '@app/tables/nutrients';
import nutrKeys from '@app/tables/nutrient-order';
import { roundForHumans } from '@utils/Math';
import { getNutrientsToDisplay } from '@pro/utils/Patients';
import { useMealPlanData } from '../../contexts/MealPlanContext';

import './NutritionModal.scss';

const NutritionModal = ({ closeModal }) => {
    const { profile, analysis } = useMealPlanData();

    // Extract nutrients from context
    const dailyNutrients = analysis?.nutrients || {};
    const nutrientsBy = analysis?.nutrients_by_offset || {};
    const displayNutrs = getNutrientsToDisplay({...profile, portion: 1}, dailyNutrients);

    const nutrNos = Object.keys(displayNutrs).sort((a, b) => {
        const aI = nutrKeys.indexOf(a),
              bI = nutrKeys.indexOf(b);

        if (aI > bI) return 1;
        if (aI < bI) return -1;
        return 0;
    });

    // Get daily totals from profile preferences
    const dailyTotals = profile?.preferences?.daily_totals || {};

    // Get the day offsets from nutrients object - it should have structure like {0: {...}, 1: {...}, 2: {...}}
    let dayOffsets = Object.keys(nutrientsBy).filter(key => !isNaN(parseInt(key))).sort((a, b) => parseInt(a) - parseInt(b));

    // If no numeric keys found (nutrientsBy might be empty or incorrectly structured),
    // create a simple array from 0 to a reasonable default
    if (dayOffsets.length === 0) {
        // Try to determine max offset from the data, or default to showing at least one day
        const maxOffset = Math.max(...Object.keys(nutrientsBy).map(k => parseInt(k)).filter(n => !isNaN(n)), 0);
        dayOffsets = Array.from({ length: maxOffset + 1 }, (_, i) => String(i));
    }

    return (
        <Modal isOpen={true}
            onRequestClose={closeModal}
            closeModal={closeModal}
            className="el-modal el-modal4"
            overlayClassName="el-modal-overlay4"
            contentLabel="Daily Nutrition Breakdown"
            closeTimeoutMS={250}>

            <div className="el-modal-container el-modal4-container customizer-nutrition-container">
                <header data-has-controls={true}>
                    <button className="el-modal-back-btn" onClick={closeModal}>
                        <span>Back</span>
                    </button>

                    <h2>Daily Nutrition Breakdown</h2>
                </header>

                <div className="el-modal-body-container el-modal4-body-container">

            <table>
                <thead>
                    <tr>
                        <th className="nutrient-name">Nutrient</th>
                        {dayOffsets.map(offset => (
                            <th key={offset}>Day {parseInt(offset) + 1}</th>
                        ))}
                        <th>Daily Average</th>
                        <th>Goal Range</th>
                        <th>%</th>
                    </tr>
                </thead>
                <tbody>
                    {nutrNos.map(nutrNo => {
                        const nutrient = allNutrients[nutrNo];
                        const target = displayNutrs[nutrNo];
                        const dailyTotal = dailyTotals[nutrNo];

                        // Use daily nutrients for the daily average value
                        const averageValue = dailyNutrients[nutrNo] || 0;

                        // Calculate percentage based on the goal type
                        let percentage;
                        let goalValue;

                        if (dailyTotal && averageValue) {
                            if (dailyTotal.min > 0 && dailyTotal.max > 0) {
                                // For range goals, use the midpoint
                                goalValue = (dailyTotal.min + dailyTotal.max) / 2;
                            } else if (dailyTotal.min > 0) {
                                // For minimum goals
                                goalValue = dailyTotal.min;
                            } else if (dailyTotal.max > 0) {
                                // For maximum goals
                                goalValue = dailyTotal.max;
                            }

                            percentage = Math.round((averageValue / goalValue) * 100);
                        }

                        return (
                            <tr key={nutrNo}>
                                <th className="nutrient-name">{nutrient.NutrDesc}</th>
                                {dayOffsets.map(offset => {
                                    // Try to get the nutrient value for this day
                                    const dayData = nutrientsBy[offset];
                                    let dayValue = 0;

                                    if (dayData && dayData['all-day'] && dayData['all-day'][nutrNo]) {
                                        dayValue = dayData['all-day'][nutrNo];
                                    }

                                    return (
                                        <td key={offset}>
                                            {roundForHumans(dayValue)} <em>{nutrient.Units}</em>
                                        </td>
                                    );
                                })}
                                <td>{roundForHumans(averageValue)} <em>{nutrient.Units}</em></td>
                                <td>
                                    {dailyTotal ? (
                                        <>
                                            {dailyTotal.min > 0 && dailyTotal.max > 0 ? (
                                                <>{roundForHumans(dailyTotal.min)} - {roundForHumans(dailyTotal.max)} {nutrient.Units}</>
                                            ) : dailyTotal.min > 0 ? (
                                                <>{roundForHumans(dailyTotal.min)} {nutrient.Units} MIN</>
                                            ) : dailyTotal.max > 0 ? (
                                                <>{roundForHumans(dailyTotal.max)} {nutrient.Units} MAX</>
                                            ) : <>Tracked</>}
                                        </>
                                    ) : (
                                        <span>-</span>
                                    )}
                                </td>
                                <td>
                                    {(dailyTotal && averageValue && goalValue) ?
                                        <>
                                            {percentage}% of {roundForHumans(goalValue)} {nutrient.Units}
                                        </>
                                    : <span>-</span>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

                </div>
            </div>
        </Modal>
    );
};

NutritionModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
};

export default NutritionModal;
